import { useEffect, useState } from 'react';
import {
  BrainIcon,
  PencilIcon,
  PhoneForwardedIcon,
  PlusIcon,
  TagIcon,
  Trash2Icon,
  XCircleIcon,
  XIcon,
} from 'lucide-react';

import { EscalationCallout } from '@/app/(app)/voice-agent/tab-routing';
import { ToggleButton } from '@/components/atoms/toggle-button';
import { ApiSchemas } from '@/components/providers/v1-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import { useVoiceSettingsCtx } from './provider';

type Behavior = ApiSchemas['Behavior'];

export function BehaviorSettings() {
  const {
    settings: { behaviors },
    onChange: onSettingsChange,
  } = useVoiceSettingsCtx();

  const onChange = (v: Partial<typeof behaviors>) => {
    onSettingsChange({ behaviors: { ...behaviors, ...v } });
  };

  const onBehaviorChange = (index: number, behavior: Partial<Behavior>) => {
    onChange({
      behaviors: [
        ...behaviors.behaviors.slice(0, index),
        { ...behaviors.behaviors[index], ...behavior },
        ...behaviors.behaviors.slice(index + 1),
      ],
    });
  };

  const onBehaviorDelete = (index: number) => {
    onChange({
      behaviors: behaviors.behaviors.filter((_, i) => i !== index),
    });
  };

  const onBehaviorAdd = (behavior: Behavior) => {
    onChange({
      behaviors: [behavior, ...behaviors.behaviors],
    });
  };

  return (
    <Card>
      <CardHeader className='flex flex-row items-center justify-between gap-6'>
        <div className='space-y-1.5'>
          <CardTitle>Agent Behaviors</CardTitle>
          <CardDescription>
            Define specific behavioral patterns and responses for your voice agent. These behaviors help the agent
            respond appropriately to different customer situations.
          </CardDescription>
        </div>
        <BehaviorDialog
          initial={{
            name: '',
            description: '',
            script: '',
            action: 'DO_NOTHING',
            ai_phrases: [],
          }}
          onSubmit={onBehaviorAdd}
          trigger={
            <Button variant='outline' size='lg'>
              <PlusIcon className='size-4' />
              Add New Behavior
            </Button>
          }
          title='Add New Behavior'
        />
      </CardHeader>
      <CardContent className='flex flex-col gap-4'>
        {behaviors.behaviors.length === 0 ? (
          <div className='text-muted-foreground py-8 text-center'>
            <BrainIcon className='mx-auto mb-4 size-12 opacity-50' />
            <p>No behaviors configured yet.</p>
            <p className='text-sm'>Add your first behavior to get started.</p>
          </div>
        ) : (
          behaviors.behaviors.map((behavior, index) => (
            <BehaviorItem
              key={index}
              behavior={behavior}
              onChange={(v) => onBehaviorChange(index, v)}
              onDelete={() => onBehaviorDelete(index)}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

interface BehaviorItemProps {
  behavior: Behavior;
  onChange: (behavior: Partial<Behavior>) => void;
  onDelete: () => void;
}

function BehaviorItem({ behavior, onChange, onDelete }: BehaviorItemProps) {
  return (
    <div className='rounded-md border p-4'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <div className='bg-muted rounded-md p-3'>
            <BrainIcon className='size-4' />
          </div>
          <div className=''>
            <div className='text-sm font-medium'>{behavior.name}</div>
            {behavior.description && <div className='text-muted-foreground text-xs'>{behavior.description}</div>}
          </div>
        </div>
        <div className='flex items-center gap-2'>
          <BehaviorDialog
            initial={behavior}
            onSubmit={onChange}
            trigger={
              <Button variant='ghost' size='icon'>
                <PencilIcon className='size-4' />
              </Button>
            }
            title='Edit Behavior'
          />
          <Button variant='ghost' size='icon' onClick={onDelete}>
            <Trash2Icon className='size-4' />
          </Button>
        </div>
      </div>
    </div>
  );
}

interface BehaviorDialogProps {
  initial: Behavior;
  onSubmit: (behavior: Behavior) => void;
  trigger: React.ReactNode;
  title: string;
}

function BehaviorDialog({ initial, onSubmit, trigger, title }: BehaviorDialogProps) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<Behavior>(initial);
  const [phraseInput, setPhraseInput] = useState('');

  // Reset state when dialog opens or initial values change
  useEffect(() => {
    setState(initial);
    setPhraseInput('');
  }, [initial, open]);

  const onChange = (v: Partial<Behavior>) => {
    setState((prev) => ({ ...prev, ...v }));
  };

  const handleSubmit = () => {
    onSubmit(state);
    setOpen(false);
  };

  const addPhrase = () => {
    const trimmedPhrase = phraseInput.trim();
    if (trimmedPhrase && !state.ai_phrases?.includes(trimmedPhrase)) {
      onChange({
        ai_phrases: [...(state.ai_phrases || []), trimmedPhrase],
      });
      setPhraseInput('');
    }
  };

  const removePhrase = (phraseToRemove: string) => {
    onChange({
      ai_phrases: state.ai_phrases?.filter((phrase) => phrase !== phraseToRemove) || [],
    });
  };

  const handlePhraseKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addPhrase();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent showClose className='max-h-[90vh] max-w-2xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Configure how the agent should behave in specific situations. Define responses, actions, and escalation
            procedures.
          </DialogDescription>
        </DialogHeader>
        <div className='flex flex-col gap-4'>
          <div className='space-y-1.5'>
            <Label>Behavior Name</Label>
            <Input
              value={state.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder='e.g. Frustrated Customer, Pricing Inquiry'
            />
          </div>
          <div className='space-y-1.5'>
            <Label>Description</Label>
            <Textarea
              value={state.description || ''}
              onChange={(e) => onChange({ description: e.target.value })}
              placeholder='Describe when this behavior should be used...'
              rows={2}
            />
          </div>
          <div className='space-y-1.5'>
            <Label>Response Script</Label>
            <Textarea
              value={state.script}
              onChange={(e) => onChange({ script: e.target.value })}
              placeholder='Enter the script the agent should use...'
              rows={4}
            />
            <p className='text-muted-foreground text-sm'>
              This is the primary script AI uses for this behavior. If the action below is &apos;Transfer&apos;, this
              script should naturally lead into it.
            </p>
          </div>
          <div className='space-y-1.5'>
            <Label>Phrases to use</Label>
            <div className='flex gap-2'>
              <Input
                value={phraseInput}
                onChange={(e) => setPhraseInput(e.target.value)}
                onKeyDown={handlePhraseKeyDown}
                placeholder='Enter a phrase...'
                className='flex-1'
              />
              <Button onClick={addPhrase} variant='outline' size='icon' disabled={!phraseInput.trim()}>
                <PlusIcon className='size-4' />
              </Button>
            </div>
            <p className='text-muted-foreground text-sm'>
              Enter short phrases the agent can use in this situation. These complement the main Response Script.
            </p>
            {state.ai_phrases && state.ai_phrases.length > 0 && (
              <div className='mt-2 flex flex-wrap gap-2'>
                {state.ai_phrases.map((phrase, index) => (
                  <Badge key={index} variant='secondary' className='flex items-center gap-1'>
                    <TagIcon className='size-3' />
                    {phrase}
                    <button
                      className='hover:bg-destructive/10 ml-1 rounded-md p-0.5'
                      onClick={() => removePhrase(phrase)}
                    >
                      <XIcon className='text-destructive size-3' />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className='space-y-1.5'>
            <Label>Action to Take</Label>
            <div className='grid grid-cols-2 gap-2'>
              <ToggleButton
                selected={state.action === 'DO_NOTHING'}
                toggle={() => onChange({ action: 'DO_NOTHING' })}
                className='flex-col items-center justify-center py-4'
              >
                <XCircleIcon className='size-4' />
                Do Nothing
              </ToggleButton>
              <ToggleButton
                selected={state.action === 'NOTIFY_REPRESENTATIVE'}
                toggle={() => onChange({ action: 'NOTIFY_REPRESENTATIVE' })}
                className='flex-col items-center justify-center py-4'
              >
                <PhoneForwardedIcon className='size-4' />
                Notify Representative
              </ToggleButton>
            </div>
            {state.action === 'NOTIFY_REPRESENTATIVE' && (
              <div className='mt-4'>
                <EscalationCallout />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={handleSubmit} disabled={!state.name.trim()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
